import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../shared/api';
import { formatDate } from '../shared/formatting';
import { Avatar } from './profileModule';
import { matchesQuickSearch, QuickSearchInput } from '../shared/quickSearch';
import { Crumbs, Card as UiCard, TextInput, Select as UiSelect, Pill, ui } from '../shared/ui';

type DirectoryRow = {
    id: number;
    name: string;
    email: string | null;
    photo_url: string | null;
    job_title: string | null;
    phone_business: string | null;
    date_of_birth: string | null;
    shares_birthday: boolean;
    department_label: string | null;
    department_region: string | null;
    department_city: string | null;
    company_code: string | null;
    company_name: string | null;
};

export function DirectoryModule({ token, locale }: { token: string; locale: 'hr' | 'en' }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [rows, setRows] = useState<DirectoryRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [query, setQuery] = useState('');
    const [companyFilter, setCompanyFilter] = useState('');

    useEffect(() => {
        setLoading(true);
        api<{ rows: DirectoryRow[] }>('/directory', token)
            .then((r) => setRows(r.rows))
            .catch((e) => setError(e instanceof Error ? e.message : 'API'))
            .finally(() => setLoading(false));
    }, [token]);

    const companies = useMemo(() => {
        const set = new Map<string, string>();
        for (const r of rows) {
            if (r.company_code) set.set(r.company_code, r.company_name ?? r.company_code);
        }
        return Array.from(set.entries()).sort();
    }, [rows]);

    const filtered = useMemo(() => {
        const cmp = (a: string, b: string) => a.localeCompare(b, locale === 'en' ? 'en' : 'hr', { sensitivity: 'base' });
        return rows
            .filter((r) => {
                if (companyFilter && r.company_code !== companyFilter) return false;
                return matchesQuickSearch([r.name, r.email, r.job_title, r.department_label, r.department_region, r.department_city, r.company_code, r.company_name, r.phone_business], query);
            })
            .slice()
            .sort((a, b) => {
                // Sort: city → branch_label → name (regija je gruba dimenzija, ide kao group header niže)
                const byCity = cmp(a.department_city ?? '￿', b.department_city ?? '￿');
                if (byCity !== 0) return byCity;
                const byBranch = cmp(a.department_label ?? '￿', b.department_label ?? '￿');
                if (byBranch !== 0) return byBranch;
                return cmp(a.name, b.name);
            });
    }, [rows, companyFilter, query, locale]);

    // Klasifikacija per redak: POSL_ prefix → Poslovnice, ostalo (npr. "1001 · Uprava") → Uprava, nepoznato → Ostalo
    function classifyRow(r: DirectoryRow): 'poslovnice' | 'uprava' | 'ostalo' {
        const d = (r.department_label ?? '').trim().toUpperCase();
        if (d.startsWith('POSL')) return 'poslovnice';
        if (/^\d{3,5}/.test(d)) return 'uprava';
        return 'ostalo';
    }

    // Grupiranje: kategorija → grupni ključ (regija za poslovnice, tvrtka inače) → niz korisnika
    const noRegionLabel = locale === 'en' ? 'No region' : 'Bez regije';
    const grouped = useMemo(() => {
        const out: Record<'poslovnice' | 'uprava' | 'ostalo', Record<string, DirectoryRow[]>> = {
            poslovnice: {}, uprava: {}, ostalo: {},
        };
        for (const r of filtered) {
            const cat = classifyRow(r);
            const groupKey = cat === 'poslovnice'
                ? (r.department_region ?? noRegionLabel)
                : (r.company_code ?? '—');
            if (! out[cat][groupKey]) out[cat][groupKey] = [];
            out[cat][groupKey].push(r);
        }
        return out;
    }, [filtered, noRegionLabel]);

    const categoryMeta: Array<{ key: 'poslovnice' | 'uprava' | 'ostalo'; labelHr: string; labelEn: string; icon: string; color: string }> = [
        { key: 'uprava', labelHr: 'Uprava', labelEn: 'Administration', icon: '🏛', color: 'var(--ad-ink)' },
        { key: 'poslovnice', labelHr: 'Poslovnice', labelEn: 'Branches', icon: '🏪', color: '#1f7a36' },
        { key: 'ostalo', labelHr: 'Ostalo', labelEn: 'Other', icon: '👥', color: 'var(--ad-acc-3)' },
    ];

    const isHrms = typeof document !== 'undefined' && document.body.dataset.theme === 'hrms';

    // Zajedničko renderiranje grupiranih kartica (poslovnice/uprava/ostalo) — koristi se i u legacy i u hrms grani.
    const renderCategories = () => categoryMeta.map((cat) => {
        const byCompany = grouped[cat.key];
        const groupKeys = Object.keys(byCompany).sort((a, b) => {
            // Za poslovnice: regije A–Z, "Bez regije" na kraj.
            if (cat.key === 'poslovnice') {
                if (a === noRegionLabel) return 1;
                if (b === noRegionLabel) return -1;
            }
            return a.localeCompare(b, locale === 'en' ? 'en' : 'hr', { sensitivity: 'base' });
        });
        const totalInCat = groupKeys.reduce((sum, c) => sum + byCompany[c].length, 0);
        if (totalInCat === 0) return null;
        return (
            <section key={cat.key} style={{ marginBottom: 28 }}>
                <div style={{
                    borderBottom: `2px solid ${cat.color}`,
                    padding: '8px 12px',
                    marginBottom: 12,
                    background: `linear-gradient(90deg, ${cat.color}18, transparent)`,
                    borderRadius: '6px 6px 0 0',
                }}>
                    <h3 style={{ margin: 0, color: cat.color, fontSize: '1.1rem' }}>
                        {cat.icon} {t(cat.labelHr, cat.labelEn)}
                        <span className="muted" style={{ fontSize: '0.78rem', marginLeft: 8, fontWeight: 400 }}>{totalInCat}</span>
                    </h3>
                </div>
                {groupKeys.map((groupKey) => {
                    const items = byCompany[groupKey];
                    const isPoslovnice = cat.key === 'poslovnice';
                    const companyName = items[0]?.company_name ?? groupKey;
                    const headerIcon = isPoslovnice ? '🌍' : '🏢';
                    const headerText = isPoslovnice
                        ? groupKey
                        : `${groupKey} · ${companyName}`;
                    return (
                        <div key={groupKey} style={{ marginBottom: 18 }}>
                            {groupKeys.length > 1 || cat.key !== 'uprava' ? (
                                <div style={{ fontSize: '0.82rem', color: cat.color, fontWeight: 700, marginBottom: 6, paddingLeft: 4 }}>
                                    {headerIcon} {headerText}
                                    <span className="muted" style={{ fontSize: '0.72rem', marginLeft: 6, fontWeight: 400 }}>{items.length}</span>
                                </div>
                            ) : null}
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                                gap: 14,
                            }}>
                                {items.map((r) => (
                                    <div key={r.id} style={{
                                        background: 'rgba(255,255,255,0.86)',
                                        border: '1px solid rgba(var(--ad-soft-rgb),0.25)',
                                        borderRadius: 14,
                                        padding: '14px 16px',
                                        display: 'flex',
                                        gap: 12,
                                        alignItems: 'flex-start',
                                    }}>
                                        <Avatar user={{ name: r.name, profile: { photo_url: r.photo_url } }} size={54} />
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ fontWeight: 700, fontSize: '0.98rem', color: '#34383d' }}>{r.name}</div>
                                            {r.job_title ? <div style={{ fontSize: '0.82rem', color: 'var(--ad-ink)', fontWeight: 600, marginTop: 2 }}>{r.job_title}</div> : null}
                                            {r.department_label ? <div className="muted" style={{ fontSize: '0.78rem', marginTop: 4 }}>🏪 {r.department_label}</div> : null}
                                            <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 3, fontSize: '0.82rem' }}>
                                                {r.email ? <div>📧 <a href={`mailto:${r.email}`} style={{ color: 'var(--ad-ink)', textDecoration: 'none' }}>{r.email}</a></div> : null}
                                                {r.phone_business ? <div>📞 <a href={`tel:${r.phone_business}`} style={{ color: 'var(--ad-ink)', textDecoration: 'none' }}>{r.phone_business}</a></div> : null}
                                                {r.date_of_birth ? <div>🎂 {formatDate(r.date_of_birth, locale)}</div> : null}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </section>
        );
    });

    if (isHrms) {
        return (
            <div style={{ padding: '8px 4px 40px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 13, marginBottom: 20 }}>
                    <Crumbs items={[t('Dashboard', 'Dashboard'), t('Imenik', 'Directory')]} />
                </div>

                {error ? <div style={{ color: '#dc2626', fontSize: '0.86rem', marginBottom: 12 }}>{error}</div> : null}
                {loading ? <div style={{ color: ui.muted, fontSize: '0.86rem', marginBottom: 12 }}>{t('Učitavam…', 'Loading…')}</div> : null}

                {!loading ? (
                    <>
                        <UiCard pad={14} style={{ marginBottom: 18 }}>
                            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                                <UiSelect value={companyFilter} onChange={setCompanyFilter}>
                                    <option value="">{t('Sve tvrtke', 'All companies')}</option>
                                    {companies.map(([code, name]) => <option key={code} value={code}>{code} · {name}</option>)}
                                </UiSelect>
                                <TextInput value={query} onChange={setQuery} icon="🔍" placeholder={t('Pretraga: ime, mail, radno mjesto…', 'Search: name, email, job title…')} width={320} />
                                <div style={{ flex: 1 }} />
                                <Pill tone="info">{t('Prikazano', 'Showing')}: {filtered.length} / {rows.length}</Pill>
                            </div>
                        </UiCard>

                        {filtered.length === 0 ? (
                            <UiCard pad={24} style={{ textAlign: 'center' }}>
                                <span style={{ color: ui.muted, fontStyle: 'italic' }}>
                                    {rows.length === 0
                                        ? t('Imenik je prazan. Superadmin još nije nikoga uključio.', 'Directory is empty. Superadmin has not added anyone yet.')
                                        : t('Nema rezultata za odabrane filtere.', 'No results for the selected filters.')}
                                </span>
                            </UiCard>
                        ) : null}

                        {renderCategories()}
                    </>
                ) : null}
            </div>
        );
    }

    return (
        <section className="card admin-block">
            <div className="bi-module-hero">
                <div className="bi-module-hero-icon">📖</div>
                <div className="bi-module-hero-text">
                    <h1>{t('Imenik suradnika', 'Staff directory')}</h1>
                    <p className="muted">
                        {t(
                            'Interni popis kolega — radno mjesto, matična poslovnica, kontakti. Datum rođenja je vidljiv samo ako je korisnik to omogućio u svom profilu.',
                            'Internal staff list — job title, branch, contacts. Date of birth is visible only if the user opted in via profile.',
                        )}
                    </p>
                </div>
            </div>

            {error ? <p className="error">{error}</p> : null}
            {loading ? <p className="muted">{t('Učitavam…', 'Loading…')}</p> : null}

            {!loading ? (
                <>
                    <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
                        <select value={companyFilter} onChange={(e) => setCompanyFilter(e.target.value)} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid rgba(var(--ad-soft-rgb),0.4)' }}>
                            <option value="">{t('Sve tvrtke', 'All companies')}</option>
                            {companies.map(([code, name]) => <option key={code} value={code}>{code} · {name}</option>)}
                        </select>
                        <QuickSearchInput value={query} onChange={setQuery} placeholder={t('Pretraga: ime, mail, radno mjesto…', 'Search: name, email, job title…')} />
                        <span className="muted" style={{ fontSize: '0.82rem' }}>{t('Prikazano', 'Showing')}: <strong>{filtered.length}</strong> / {rows.length}</span>
                    </div>

                    {filtered.length === 0 ? (
                        <p className="muted" style={{ fontStyle: 'italic', textAlign: 'center', padding: '1.5rem' }}>
                            {rows.length === 0
                                ? t('Imenik je prazan. Superadmin još nije nikoga uključio.', 'Directory is empty. Superadmin has not added anyone yet.')
                                : t('Nema rezultata za odabrane filtere.', 'No results for the selected filters.')}
                        </p>
                    ) : null}

                    {categoryMeta.map((cat) => {
                        const byCompany = grouped[cat.key];
                        const groupKeys = Object.keys(byCompany).sort((a, b) => {
                            // Za poslovnice: regije A–Z, "Bez regije" na kraj.
                            if (cat.key === 'poslovnice') {
                                if (a === noRegionLabel) return 1;
                                if (b === noRegionLabel) return -1;
                            }
                            return a.localeCompare(b, locale === 'en' ? 'en' : 'hr', { sensitivity: 'base' });
                        });
                        const totalInCat = groupKeys.reduce((sum, c) => sum + byCompany[c].length, 0);
                        if (totalInCat === 0) return null;
                        return (
                            <section key={cat.key} style={{ marginBottom: 28 }}>
                                <div style={{
                                    borderBottom: `2px solid ${cat.color}`,
                                    padding: '8px 12px',
                                    marginBottom: 12,
                                    background: `linear-gradient(90deg, ${cat.color}18, transparent)`,
                                    borderRadius: '6px 6px 0 0',
                                }}>
                                    <h3 style={{ margin: 0, color: cat.color, fontSize: '1.1rem' }}>
                                        {cat.icon} {t(cat.labelHr, cat.labelEn)}
                                        <span className="muted" style={{ fontSize: '0.78rem', marginLeft: 8, fontWeight: 400 }}>{totalInCat}</span>
                                    </h3>
                                </div>
                                {groupKeys.map((groupKey) => {
                                    const items = byCompany[groupKey];
                                    const isPoslovnice = cat.key === 'poslovnice';
                                    const companyName = items[0]?.company_name ?? groupKey;
                                    const headerIcon = isPoslovnice ? '🌍' : '🏢';
                                    const headerText = isPoslovnice
                                        ? groupKey
                                        : `${groupKey} · ${companyName}`;
                                    return (
                                        <div key={groupKey} style={{ marginBottom: 18 }}>
                                            {groupKeys.length > 1 || cat.key !== 'uprava' ? (
                                                <div style={{ fontSize: '0.82rem', color: cat.color, fontWeight: 700, marginBottom: 6, paddingLeft: 4 }}>
                                                    {headerIcon} {headerText}
                                                    <span className="muted" style={{ fontSize: '0.72rem', marginLeft: 6, fontWeight: 400 }}>{items.length}</span>
                                                </div>
                                            ) : null}
                                            <div style={{
                                                display: 'grid',
                                                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                                                gap: 14,
                                            }}>
                                                {items.map((r) => (
                                                    <div key={r.id} style={{
                                                        background: 'rgba(255,255,255,0.86)',
                                                        border: '1px solid rgba(var(--ad-soft-rgb),0.25)',
                                                        borderRadius: 14,
                                                        padding: '14px 16px',
                                                        display: 'flex',
                                                        gap: 12,
                                                        alignItems: 'flex-start',
                                                    }}>
                                                        <Avatar user={{ name: r.name, profile: { photo_url: r.photo_url } }} size={54} />
                                                        <div style={{ flex: 1, minWidth: 0 }}>
                                                            <div style={{ fontWeight: 700, fontSize: '0.98rem', color: '#34383d' }}>{r.name}</div>
                                                            {r.job_title ? <div style={{ fontSize: '0.82rem', color: 'var(--ad-ink)', fontWeight: 600, marginTop: 2 }}>{r.job_title}</div> : null}
                                                            {r.department_label ? <div className="muted" style={{ fontSize: '0.78rem', marginTop: 4 }}>🏪 {r.department_label}</div> : null}
                                                            <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 3, fontSize: '0.82rem' }}>
                                                                {r.email ? <div>📧 <a href={`mailto:${r.email}`} style={{ color: 'var(--ad-ink)', textDecoration: 'none' }}>{r.email}</a></div> : null}
                                                                {r.phone_business ? <div>📞 <a href={`tel:${r.phone_business}`} style={{ color: 'var(--ad-ink)', textDecoration: 'none' }}>{r.phone_business}</a></div> : null}
                                                                {r.date_of_birth ? <div>🎂 {formatDate(r.date_of_birth, locale)}</div> : null}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })}
                            </section>
                        );
                    })}
                </>
            ) : null}
        </section>
    );
}
